import * as React from 'react';

export type XAxisType = 'date' | 'number' | 'category';
export type RangeKey = '7d' | '30d' | '90d' | 'all';

/**
 * Config for a single metric/series.
 */
export interface MetricSeriesConfig<TData extends object> {
    id: string; // unique id + chart dataKey
    key: keyof TData; // raw data ka key
    label: string; // legend / tooltip label
    color?: string; // optional color, UI default de sakta hai
    strokeWidth?: number; // line thickness
    variant?: 'solid' | 'dashed';
    renderAs?: 'line' | 'bar' | 'area'; // cartesian types ke liye
}

export interface UseMetricChartOptions<TData extends object> {
    data: TData[];

    /** dimension / x-axis key */
    dimensionKey: keyof TData;
    dimensionType?: XAxisType;

    series: MetricSeriesConfig<TData>[];

    /** date range filter enable/disable (default true for date dims) */
    enableRangeFilter?: boolean;
    initialRange?: RangeKey;

    /** custom sort override; */
    sortComparator?: (a: TData, b: TData) => number;
}

export interface UseMetricChartResult<TData extends object> {
    /** normalized data: x + per-series values */
    chartData: Array<{ x: any } & Record<string, number | null>>;

    /** same series config + active flag */
    seriesWithState: Array<MetricSeriesConfig<TData> & { active: boolean }>;
    toggleSeries: (id: string) => void;

    range: RangeKey;
    setRange: (range: RangeKey) => void;

    dimensionType: XAxisType;
}

/**
 * PURE hook –
 *  - sort
 *  - optional date range filter
 *  - normalized shape { x, [seriesId]: number }
 *  - series active/inactive toggles
 */
export function useMetricChart<TData extends object>(
    options: UseMetricChartOptions<TData>,
): UseMetricChartResult<TData> {
    const {
        data,
        dimensionKey,
        dimensionType = 'date',
        series,
        enableRangeFilter,
        initialRange = 'all',
        sortComparator,
    } = options;

    const [range, setRange] = React.useState<RangeKey>(initialRange);
    const [activeIds, setActiveIds] = React.useState<string[]>(() =>
        series.map((s) => s.id),
    );

    const rangeEnabled =
        enableRangeFilter ?? (dimensionType === 'date' ? true : false);

    // keep activeIds in sync with available series
    React.useEffect(() => {
        setActiveIds((prev) => {
            const valid = series.map((s) => s.id);
            const current = prev.filter((id) => valid.includes(id));
            return current.length ? current : valid;
        });
    }, [series]);

    // ---------- SORT ----------
    const sorted = React.useMemo(() => {
        const copy = [...data];
        if (sortComparator) {
            copy.sort(sortComparator);
            return copy;
        }

        copy.sort((a, b) => {
            const av = a[dimensionKey] as any;
            const bv = b[dimensionKey] as any;

            if (dimensionType === 'date') {
                const at = new Date(av).getTime();
                const bt = new Date(bv).getTime();
                return at - bt;
            }

            if (typeof av === 'number' && typeof bv === 'number') {
                return av - bv;
            }

            // category -> original order
            return 0;
        });

        return copy;
    }, [data, sortComparator, dimensionKey, dimensionType]);

    // ---------- RANGE FILTER (optional) ----------
    const ranged = React.useMemo(() => {
        if (!rangeEnabled || dimensionType !== 'date') return sorted;
        if (range === 'all' || sorted.length === 0) return sorted;

        const lastVal = sorted[sorted.length - 1][dimensionKey] as any;
        const end = new Date(lastVal).getTime();
        const days =
            range === '7d'
                ? 7
                : range === '30d'
                  ? 30
                  : range === '90d'
                    ? 90
                    : 9999;
        const start = end - days * 24 * 60 * 60 * 1000;

        return sorted.filter((item) => {
            const t = new Date(item[dimensionKey] as any).getTime();
            return t >= start;
        });
    }, [sorted, range, rangeEnabled, dimensionKey, dimensionType]);

    // ---------- SHAPE conversion ----------
    const chartData = React.useMemo(() => {
        return ranged.map((item) => {
            const base: any = { x: item[dimensionKey] };
            series.forEach((s) => {
                const raw = item[s.key];
                base[s.id] =
                    typeof raw === 'number'
                        ? (raw as number)
                        : raw != null
                          ? Number(raw)
                          : null;
            });
            return base;
        });
    }, [ranged, series, dimensionKey]);

    // ---------- SERIES STATE ----------
    const seriesWithState = React.useMemo(
        () =>
            series.map((s) => ({
                ...s,
                active: activeIds.includes(s.id),
            })),
        [series, activeIds],
    );

    const toggleSeries = (id: string) => {
        setActiveIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    return {
        chartData,
        seriesWithState,
        toggleSeries,
        range,
        setRange,
        dimensionType,
    };
}
